import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertTriangle, Lock, Ban } from 'lucide-react';
import type { PublishChecklist, ProducerMeta } from '@/lib/capabilities';

interface PublishReadinessCardProps {
  farmId: string;
  meta: ProducerMeta;
  checklist: PublishChecklist;
  canPublish: boolean;
  canUnpublish: boolean;
}

interface CheckItem {
  key: string;
  label: string;
  met: boolean;
  link?: string;
  linkLabel?: string;
}

function getVisibilityCheckItems(checklist: PublishChecklist, farmId: string): CheckItem[] {
  return [
    {
      key: 'description',
      label: 'Namn och beskrivning',
      met: checklist.hasNameAndDescription,
      link: `/app/producer/farm/${farmId}/grundinfo`,
      linkLabel: 'Redigera grundinfo',
    },
    {
      key: 'place',
      label: 'Kopplad plats',
      met: checklist.hasLinkedPlace,
      link: `/app/producer/farm/${farmId}/plats`,
      linkLabel: 'Välj plats',
    },
    {
      key: 'cover',
      label: 'Omslagsbild (rekommenderas)',
      met: checklist.hasCoverImage,
      link: `/app/producer/farm/${farmId}/bilder`,
      linkLabel: 'Lägg till bild',
    },
  ];
}

/**
 * Visibility readiness section – shows checklist for becoming publicly visible.
 * Verification is NOT required for visibility, only for selling.
 */
export function PublishReadinessCard({
  farmId,
  meta,
  checklist,
  canPublish: canPublishProp,
  canUnpublish,
}: PublishReadinessCardProps) {
  const checks = getVisibilityCheckItems(checklist, farmId);
  // Cover image is recommended but not blocking
  const blockingChecks = checks.filter((c) => c.key !== 'cover');

  // ── Suspended ──
  if (meta.isSuspended) {
    return (
      <Alert className="border-destructive/30 bg-destructive/5">
        <Ban className="h-4 w-4 text-destructive" />
        <AlertTitle className="text-destructive flex items-center gap-2">
          Avstängd
          <Badge variant="destructive">Ej tillgänglig</Badge>
        </AlertTitle>
        <AlertDescription className="text-destructive/80 mt-1">
          Din gård är avstängd och inte synlig för kunder. Kontakta support för mer information.
        </AlertDescription>
      </Alert>
    );
  }

  // ── Currently published ──
  if (meta.isPublic) {
    return (
      <Alert className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20">
        <Eye className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <AlertTitle className="text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
          Synlig för kunder
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
            Publik
          </Badge>
        </AlertTitle>
        <AlertDescription className="text-emerald-700 dark:text-emerald-300 mt-1">
          Din verksamhet är publik på Farmly och syns kopplad till sin plats.
        </AlertDescription>
      </Alert>
    );
  }

  // ── Not published, visibility requirements met (ready to publish) ──
  if (canPublishProp) {
    return (
      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
        <EyeOff className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertTitle className="text-blue-800 dark:text-blue-200 flex items-center gap-2">
          Redo att bli synlig
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
            Inte synlig
          </Badge>
        </AlertTitle>
        <AlertDescription className="text-blue-700 dark:text-blue-300 mt-2">
          <p className="text-sm">
            Alla krav för synlighet är uppfyllda. Gå till Publicering för att göra gården synlig.
          </p>
          <Button size="sm" className="mt-3" asChild>
            <Link to={`/app/producer/farm/${farmId}/publicering`}>
              <Eye className="h-4 w-4 mr-2" />
              Gör synlig
            </Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // ── Not published, missing requirements ──
  return (
    <Alert className="border-muted bg-muted/30">
      <Lock className="h-4 w-4 text-muted-foreground" />
      <AlertTitle className="text-foreground flex items-center gap-2">
        Synlighet
        <Badge variant="outline">Inte synlig</Badge>
      </AlertTitle>
      <AlertDescription className="text-muted-foreground mt-2 space-y-3">
        <p className="text-sm">
          För att synas för besökare behöver din gård uppfylla följande:
        </p>
        <ul className="space-y-1.5 text-sm">
          {checks.map((c) => (
            <li key={c.key} className="flex items-center gap-2">
              {c.met ? (
                <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
              )}
              <span className={c.met ? 'text-muted-foreground' : 'text-foreground font-medium'}>
                {c.label}
              </span>
              {!c.met && c.link && (
                <Button variant="link" size="sm" className="h-auto p-0 ml-auto text-xs" asChild>
                  <Link to={c.link}>{c.linkLabel}</Link>
                </Button>
              )}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
